import { PropsWithChildren, FC, useState } from 'react'
import { PlusOutlined, TeamOutlined } from '@ant-design/icons'
import { Col, Tooltip } from 'antd'
import { useAddFollowersFlag } from '@OpsWb/logic'
import {
    adminJobRequestQuery_jobRequest_subjectTypes as SubjectType,
    adminJobRequestQuery_jobRequest_jobRequestComments as MessageType,
} from '@OpsQuality/JobRequestDetails/__generated__/adminJobRequestQuery'

import Box from '~/core/Box'
import Button from '~/core/Button'
import colors from '~/styles/_colors.css'
import {
    FollowerUser,
    DriFragment as MfgDriType,
    ActiveMfgDrisQuery,
} from '~/gql/graphql'

import MessagesModal from './components/MessagesModal'
import AddFollowersModal from './components/AddFollowers'
import { useFollowersData } from './components/logic'
import styles from './components/styles.css'
import RenderMessage from './components/RenderMessage'

type MessagesPropsType = {
    subjectTypes?: SubjectType[]
    itemPartNames?: string[]
    jobRequestId?: number | null
    refetchQuery: any
    successContent: { title: string }
    partName?: string
    usedInScheduling?: boolean
    messages?: MessageType[]
    followers?: FollowerUser[]
    maxHeight?: number
    jobName?: string
    isSecurePlus?: boolean
    mfgDri?: MfgDriType | null
}

export const Messages: FC<PropsWithChildren<MessagesPropsType>> = props => {
    const {
        subjectTypes,
        itemPartNames,
        jobRequestId,
        refetchQuery,
        successContent,
        partName,
        messages,
        usedInScheduling,
        followers,
        maxHeight,
        jobName,
        isSecurePlus,
        mfgDri,
    } = props
    const [isOpen, setIsOpen] = useState(false)
    const [isModalVisible, setIsModalVisible] = useState(false)
    const addFollowersFlag = useAddFollowersFlag()
    const eligibleFollowers = useFollowersData()
    const messageScrollHeight = maxHeight
        ? `${Math.floor(maxHeight) - 125}px`
        : undefined

    const showModal = () => {
        setIsOpen(true)
    }

    const closeModal = () => {
        setIsOpen(false)
    }

    const showFollowerModal = () => {
        setIsModalVisible(true)
    }

    const handleModalClose = () => {
        setIsModalVisible(false)
    }

    return (
        <Box
            className={styles.messagesCard}
            backgroundColor={colors.white}
            border={`solid 1px ${colors.topNavHeaderBorder}`}
            direction="col"
            margin={usedInScheduling ? '0px' : '24px 0'}
        >
            <Box
                padding="12px"
                style={{
                    backgroundColor: usedInScheduling ? colors.grey2 : '',
                    borderRadius: '8px',
                }}
            >
                <Col span={22}>
                    <Button
                        name="sendMessageBtn"
                        placement="messages-tab-panel"
                        type={!usedInScheduling ? 'primary' : undefined}
                        onClick={showModal}
                        data-test-target="send-message-button"
                    >
                        {usedInScheduling ? 'Add new message' : 'Send Message'}
                    </Button>
                </Col>
                {usedInScheduling && (
                    <Col
                        span={3}
                        style={{ textAlign: 'center', alignSelf: 'center' }}
                    >
                        {addFollowersFlag && (
                            <span onClick={showFollowerModal}>
                                <Tooltip title="Add followers">
                                    <PlusOutlined
                                        className={styles.addFollowersIconStyle}
                                    />
                                </Tooltip>
                            </span>
                        )}
                        <Tooltip
                            title={followers?.map((follower, index) => (
                                <span key={index} style={{ fontWeight: 400 }}>
                                    {follower.name}
                                    <br />
                                </span>
                            ))}
                            trigger="hover"
                            placement="bottom"
                        >
                            <TeamOutlined
                                className={styles.addFollowersIconStyle}
                            />
                        </Tooltip>
                    </Col>
                )}
            </Box>
            <Box
                direction="col"
                maxHeight={messageScrollHeight}
                overflowY="scroll"
            >
                {messages?.map((message, index) => {
                    return <RenderMessage key={index} message={message} />
                })}
            </Box>
            {jobRequestId && (
                <MessagesModal
                    openMessageModal={isOpen}
                    jobRequestId={jobRequestId}
                    subjectTypes={subjectTypes}
                    onClose={closeModal}
                    itemPartNames={itemPartNames}
                    refetchQuery={refetchQuery}
                    successContent={successContent}
                    partName={partName}
                    usedInScheduling={usedInScheduling}
                />
            )}
            <AddFollowersModal
                showModal={showFollowerModal}
                isModalVisible={isModalVisible}
                handleCancel={handleModalClose}
                followers={followers}
                jobName={jobName}
                isSecurePlus={isSecurePlus}
                mfgDri={
                    mfgDri as unknown as ActiveMfgDrisQuery['getActiveMfgDriUsers']
                }
                eligibleFollowers={eligibleFollowers || []}
                jobRequestId={jobRequestId}
            />
        </Box>
    )
}
export default Messages
